import React, { useMemo } from "react"
import {
  Box,
  Button,
  useTheme,
  FormControlLabel,
  Switch,
  /*   Backdrop,
  CircularProgress, */
  Avatar,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { Trans } from "react-i18next"
import { Link } from "react-router-dom"

import { getIcon } from "utils/icon"
import { useVenue } from "providers/venue"
import { useTranslation } from "providers/i18n"
import { useDirectionOverview } from "hooks/direction/useDirectionOverview"
import { useDirectionOverviewMap } from "hooks/direction/useDirectionOverviewMap"
import { useMobileUrl } from "hooks/useMobileUrl"
import { NearMeIcon, PlayArrowIcon, ShopIcon } from "components/icons"
import { OrdinalSelector } from "components/OrdinalSelector"
import { PageCard } from "components/card"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { StepByStepIcon } from "components/Progress/StepIcon"
import { QRCode } from "components/QRCode"
import { MotionDiv } from "components/motions"
import { SkeletonDirectionOverviewSidebar } from "components/domains/direction/kiosk-h"
import { MyLocationButton } from "components/Button"

export const DirectionOverviewKioskHPage = ({ Layout = Box }) => {
  const {
    /*  isCalculatingRoute, */
    currentOrdinal,
    route,
    elevatorPreferredMode,
    relatedLevelWithSteps,
    changeOrdinal,
    toggleElevatorPreference,
    startNavigate,
    destinationName,
    destinationLogo,
    destinationCategoryIconPath,
    distance,
    duration,
    steps,
    destinationData,
  } = useDirectionOverview()
  const lastStep = steps.length - 1
  const stepsAvailable = steps && steps.length > 0

  useDirectionOverviewMap(route, currentOrdinal)

  const { isMultiOrdinalVenue } = useVenue()
  const { mobileUrl } = useMobileUrl()
  const { t } = useTranslation()

  const theme = useTheme()

  const {
    root,
    stepContainer,
    ordinalSelector,
    headerWrapper,
    actionContainer,
    avoidStepButton,
    primaryButton,
    playerActionContainer,
    contentWrapper,
    qrcodeText,
    stepListContainer,
    stepListItem,
    stepListAvatarContainer,
    stepListItemText,
    qrCodeContainer,
    startButton,
    qrCode,
    sidebarContent,
    durationText,
    locationButtonContainer,
  } = theme.components[`AppDirectionOverview`]?.styleOverrides || {}

  const { destinationIcon, locationAvatar, locationText } =
    theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body, footer, playerButton } = sideBarStyleOverrides

  const { highlightedText } =
    theme.components[`DirectionStep`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const startIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.startIcon) || <NearMeIcon />,
    [theme]
  )

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <ShopIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  return (
    <Layout
      aside={
        <PageCard>
          <MotionDiv
            sx={deepmerge(wrapper, sidebarContent)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            <Box sx={body}>
              {!stepsAvailable ? (
                <SkeletonDirectionOverviewSidebar />
              ) : (
                <Box sx={root}>
                  <Box sx={headerWrapper}>
                    <Avatar
                      src={destinationLogo}
                      sx={locationAvatar}
                      variant="logo"
                    >
                      {getIcon(destinationCategoryIconPath, destinationIcon, {
                        imageIconProps: {
                          solidColorFilterId: "directionOverview_avatar_filter",
                        },
                      }) || destinationIconComp}
                    </Avatar>
                    <Typography variant="subtitle1" sx={locationText}>
                      {t(destinationName)}
                    </Typography>
                  </Box>
                  <Box sx={contentWrapper}>
                    <Typography sx={durationText} variant="h6">
                      {t("Travel", {
                        duration,
                        distance,
                      })}
                    </Typography>
                    {mobileUrl && (
                      <Box sx={qrCodeContainer}>
                        <QRCode url={mobileUrl} sx={qrCode} />
                        <Typography
                          variant="subtitle2"
                          component="p"
                          sx={qrcodeText}
                        >
                          {t("Scan Here")}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          component="p"
                          sx={qrcodeText}
                        >
                          {t("For Mobile")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box sx={stepContainer}>
                    <List sx={stepListContainer}>
                      {steps.map((step, index) => {
                        const { description } = step
                        return (
                          <ListItem
                            key={`step-${index}`}
                            sx={stepListItem}
                            component={Link}
                            to={`navigate/steps/${
                              index + 1
                            }?avoidSteps=${elevatorPreferredMode}`}
                          >
                            <ListItemAvatar sx={stepListAvatarContainer}>
                              <StepByStepIcon
                                index={index}
                                step={step}
                                lastStep={lastStep}
                              />
                            </ListItemAvatar>
                            <ListItemText
                              sx={stepListItemText}
                              primary={
                                <Trans
                                  components={{
                                    category: highlightedStepText,
                                    destination: highlightedStepText,
                                    landmark: highlightedStepText,
                                  }}
                                >
                                  {t(description.text, {
                                    category: t(description.options?.category),
                                    destination: t(
                                      description.options?.destination
                                    ),
                                    landmark: t(description.options?.landmark),
                                  })}
                                </Trans>
                              }
                            />
                          </ListItem>
                        )
                      })}
                    </List>
                  </Box>
                  <Box sx={actionContainer}>
                    <FormControlLabel
                      sx={avoidStepButton}
                      control={
                        <Switch
                          checked={elevatorPreferredMode}
                          onChange={toggleElevatorPreference}
                        />
                      }
                      label={t("Avoid Steps")}
                    />
                    <Box sx={playerActionContainer}>
                      <Button
                        variant="outlined"
                        sx={deepmerge(playerButton, primaryButton)}
                        startIcon={<PlayArrowIcon />}
                        onClick={startNavigate}
                      >
                        {t("Play")}
                      </Button>
                    </Box>
                    <Button
                      fullWidth
                      variant="contained"
                      sx={startButton}
                      onClick={startNavigate}
                      startIcon={startIcon}
                    >
                      {t("Start")}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={footer}>
              <NavigationControl SearchButtonProps={{ hidden: true }} />
            </Box>
          </MotionDiv>
        </PageCard>
      }
    >
      {/* //TODO:: find a way for adding active ordinal (selectedOrdinal) */}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            levels={relatedLevelWithSteps}
            onClickOrdinal={changeOrdinal}
            selectedOrdinal={currentOrdinal}
          />
        </Box>
      )}
      <Box sx={locationButtonContainer}>
        <MyLocationButton
          component={Link}
          to={`/maps/dir/unknown/${destinationData?.id}`}
        />
      </Box>
    </Layout>
  )
}
